/**
 * Semáforo de vencimiento — función pura, compartida entre los slots nuevos
 * de `recurso_documentos` (EPP / Vehículo / Maquinaria, F1-F3) y el retrofit
 * del badge de `persona_documentos` (F4). Ver design `sdd/recurso-documentos`
 * §8 (obs #1307) y decisión UX cerrada con el usuario (obs #1318).
 *
 * `fc-web-app` no tiene test runner: la verificación de este módulo es
 * visual, documentada por slice en `sdd/recurso-documentos/tasks`.
 */

import type { BadgeTone } from "@/components/ui/badge"

/** Umbral por DEFECTO de "por vencer", en días. Constante nombrada —
 * independiente de `notificar_dias` (que es la ventana de aviso por email de
 * CADA documento, un concepto distinto que puede o no coincidir con este).
 *
 * Espeja `max(VentanasAviso::OPCIONES)` del backend: un documento está "por
 * vencer" exactamente cuando ya entró en el rango en que el sistema PUEDE
 * avisar de él. Es el mismo criterio y el mismo número que usan
 * `ResumenTrabajadoresService::diasPorVencer()` y
 * `ResumenDocumentosRecursoService::diasPorVencer()`.
 *
 * HISTORIA — por qué cambió de 30 a 60: los dos números convivían en pantallas
 * distintas (el listado de Trabajadores plegaba el resumen del servidor con 60;
 * los slots de `recurso_documentos` medían con 30) y "nunca se veían juntos".
 * El rediseño del listado de Vehículos los puso lado a lado: la columna
 * Documentos cuenta con la ventana del servidor y el chevron de esa misma fila
 * lleva a la ficha, donde los slots contaban con la suya — el listado decía
 * "2 por vencer" y la ficha no mostraba ningún badge. Unificar en la ventana
 * del backend es lo que cierra esa contradicción.
 *
 * Sigue siendo un DEFAULT: una pantalla que muestre a la vez un contador del
 * servidor y las filas que lo componen debe pasar `meta.dias_por_vencer`
 * explícito, por si el backend cambia sus ventanas antes que este archivo. */
export const DIAS_POR_VENCER = 60

export type EstadoVencimiento = "sin_vencimiento" | "vigente" | "por_vencer" | "vencido"

/**
 * Parsea una fecha `YYYY-MM-DD` (sin hora, tal como la devuelve la API) como
 * MEDIANOCHE LOCAL del navegador.
 *
 * RIESGO cubierto acá — off-by-one de zona horaria: `new Date("2026-07-21")`
 * (constructor de `Date` con un string ISO *date-only*) se interpreta como
 * medianoche **UTC**, no local. En Chile (UTC-3/UTC-4) eso cae dentro del día
 * ANTERIOR por la tarde/noche: un documento cuyo `fecha_vencimiento` es HOY
 * se calcularía como si hubiera vencido AYER. Por eso acá se separan los
 * componentes año/mes/día a mano y se arma la fecha con
 * `new Date(year, monthIndex, day)`, que SÍ usa la zona horaria local — el
 * mismo truco que ya usa `formatDate` en `PersonaDocumentoSlot.tsx`.
 */
function parseFechaLocal(fechaIso: string): Date {
  const [year, month, day] = fechaIso.split("-").map(Number)
  return new Date(year, month - 1, day)
}

/** Medianoche local de "hoy". Mismo motivo que `parseFechaLocal`: comparar
 * fechas requiere truncar la hora, o un vencimiento a las 23:59 de hoy
 * contaría como "mañana" en una resta de milisegundos cruda. */
function hoyLocal(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/**
 * Días de diferencia entre `fechaIso` y `hoy` (positivo = vence en el
 * futuro, negativo = ya venció). Ambos operandos se truncan a medianoche
 * local antes de restar — `Math.round` (no `Math.floor`/`Math.trunc`) porque
 * un cambio de horario de verano puede volver un "día" de 23 o 25 horas, y
 * redondear absorbe esa fracción sin desviar el resultado en días enteros.
 */
export function diasHastaVencimiento(fechaIso: string, hoy: Date = hoyLocal()): number {
  const fecha = parseFechaLocal(fechaIso)
  const hoyTruncado = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate())
  const msPorDia = 24 * 60 * 60 * 1000
  return Math.round((fecha.getTime() - hoyTruncado.getTime()) / msPorDia)
}

/**
 * Estado de 4 valores a partir de `fecha_vencimiento` (`null` = "no
 * vence"). El umbral es `DIAS_POR_VENCER` salvo que el llamador pase otro.
 *
 * - `sin_vencimiento`: `fecha_vencimiento` es `null`.
 * - `vencido`: la fecha ya pasó (`dias < 0`).
 * - `por_vencer`: entre hoy y `diasPorVencer` días inclusive — un documento
 *   que vence HOY es "por vencer", nunca "vencido".
 * - `vigente`: más de `diasPorVencer` días de margen.
 *
 * `diasPorVencer` es parámetro y no constante porque el servidor publica su
 * propia ventana en `meta.dias_por_vencer` (ver nota en `DIAS_POR_VENCER`):
 * una pantalla que muestre a la vez un contador del servidor y las filas que
 * lo componen tiene que medir con la MISMA ventana, o el contador dice 2 y
 * las filas muestran 0 badges.
 */
export function estadoVencimiento(
  fechaVencimiento: string | null,
  hoy: Date = hoyLocal(),
  diasPorVencer: number = DIAS_POR_VENCER,
): EstadoVencimiento {
  if (fechaVencimiento === null) return "sin_vencimiento"

  const dias = diasHastaVencimiento(fechaVencimiento, hoy)
  if (dias < 0) return "vencido"
  if (dias <= diasPorVencer) return "por_vencer"
  return "vigente"
}

/**
 * Presentación del semáforo: etiqueta + tono del Design System.
 *
 * NOTA HISTÓRICA: esta función devolvía clases Tailwind crudas
 * (`bg-amber-100 text-amber-800`) porque, cuando se escribió, `fc-web-app` no
 * tenía tokens semánticos y el único color de estado real era `--destructive`.
 * Eso YA NO ES CIERTO: el Design System define `success` / `warning` /
 * `destructive` / `info` / `neutral` como tonos de badge (ver
 * `DESIGN-SYSTEM.md` §4.2 y `components/ui/badge.tsx`). Por eso ahora devuelve
 * un tono y no clases: el color vive en un solo lugar.
 *
 * El badge del DS es fondo suave + texto saturado del mismo tono — nunca texto
 * blanco sobre color saturado, que era lo que hacía el caso `vencido`.
 */
export function badgeVencimiento(estado: EstadoVencimiento): {
  label: string
  tone: BadgeTone
} {
  switch (estado) {
    case "vencido":
      return { label: "Vencido", tone: "destructive" }
    case "por_vencer":
      return { label: "Por vencer", tone: "warning" }
    case "vigente":
      return { label: "Vigente", tone: "success" }
    case "sin_vencimiento":
      return { label: "Sin vencimiento", tone: "neutral" }
  }
}
